import { Badge, Box, Button, Flex, Heading, Spinner } from '@radix-ui/themes';
import { WebSocketHelper } from 'classes/helpers/web-socket.helper';
import { BlurOverlay } from 'components/common/blur-overlay';
import { CommonCallout } from 'components/common/callouts';
import { CommonSelectInput } from 'components/common/form/select';
import { DaemonStateTable } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status/machine-state-section/daemon-state-table';
import { MachineStateOverview } from 'components/sections/admin-portal/machines/dialogs/settings/machine-status/machine-state-section/overview';
import { useMachineErrorsDialogState } from 'components/sections/admin-portal/machines/hooks/useMachineErrorsDialogState';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { getMachineStatusColor } from 'enums/admin-portal';
import { t } from 'i18next';
import { IMachineStateSectionDef } from 'interfaces/i-machine-settings';
import { LOCAL_TIMEZONE } from 'lib_ts/enums/datetime.enums';
import { QueueState, WsMsgType } from 'lib_ts/enums/machine-msg.enum';
import { MachineStatus } from 'lib_ts/enums/machine.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { DaemonName, DaemonNameExt } from 'lib_ts/interfaces/i-machine-daemons';
import { IMachineState } from 'lib_ts/interfaces/i-machine-state';
import { IMachineStateCurrent } from 'lib_ts/interfaces/machine-msg/i-machine-state';
import { IReadyMsg } from 'lib_ts/interfaces/machine-msg/i-ready';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useAuthStore } from 'stores/use-auth-store';
import useMachineStatusStore from 'stores/use-machine-status-store';

const MS_GRACE_PERIOD_START_MS = 3000; // how long before we start showing the loading overlay while waiting for ms/r2f data
const MS_GRACE_PERIOD_LOADING_MS = 15000; // how long before we stop waiting and set ms/r2f to undefined

const StateTable = (props: {
  activeTable: DaemonNameExt;
  ms?: IMachineStateCurrent;
  r2f?: IReadyMsg;
  loading: boolean;
  lastTarget?: IMachineState;
}) => {
  // fallback for Arcs running < 2.5
  const readyData = props.r2f?.ready_comps || props.r2f?.data;

  switch (props.activeTable) {
    case 'Machine State':
      return (
        <MachineStateOverview
          ms={props.ms}
          readyData={readyData}
          loading={props.loading}
          lastTarget={props.lastTarget}
        />
      );

    default: {
      return (
        <DaemonStateTable
          r2f={props.r2f}
          daemon={props.activeTable}
          loading={props.loading}
        />
      );
    }
  }
};

export const MachineStateSection = (props: { lastTarget?: IMachineState }) => {
  const current = useAuthStore((s) => s.current);
  const userRoleFeatureFlags = useAuthStore((s) => s.userRoleFeatureFlags);

  const queueState = useMachineStatusStore((s) => s.queueState);
  const queueWsIDs = useMachineStatusStore((s) => s.queueWsIDs);

  const machine = useMachinesPortalStore((s) => s.machine);
  const setLastMachineStatus = useMachinesPortalStore(
    (s) => s.setLastMachineStatus
  );

  const { viewSenderErrors } = useMachineErrorsDialogState();

  const [ms, setMs] = useState<IMachineStateCurrent>();
  const [r2f, setR2f] = useState<IReadyMsg>();
  const [waitingForData, setWaitingForData] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(''); // ISO date string of our last received ms/r2f message
  const [initialized, setInitialized] = useState(false);

  const [activeTable, setActiveTable] =
    useState<DaemonNameExt>('Machine State');

  const gracePeriodStartTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const gracePeriodRef = useRef<NodeJS.Timeout | null>(null);

  const inQueue = queueWsIDs.includes(current.wsID);

  // WS subscription to this machine's ms & r2f messages
  useEffect(() => {
    if (!machine?.machineID) {
      return;
    }

    // we are not yet connected to the server
    if (!inQueue || queueState === QueueState.UserDC) {
      return;
    }

    /**
     * We may get empty responses from mscurrent or r2f if the machine is part way through a state change.
     *
     * Instead of immediately setting ms & r2f to undefined, we'll wait to see if we get a valid response
     * within our grace period and render a blur overlay on top of our last known good data in the mean time.
     *
     * If we don't get a valid response after our grace period, we'll set ms & r2f to undefined and show the empty table
     */
    const startGracePeriod = (callback: () => void) => {
      endGracePeriod();

      gracePeriodStartTimeoutRef.current = setTimeout(() => {
        setWaitingForData(true);

        // if we don't get a valid response within MS_GRACE_PERIOD_LOADING_MS, we'll set ms/r2f to undefined and show empty state
        if (!gracePeriodRef.current) {
          gracePeriodRef.current = setTimeout(() => {
            setWaitingForData(false);
            callback();
          }, MS_GRACE_PERIOD_LOADING_MS);
        }
        // wait MS_GRACE_PERIOD_START_MS before showing the loading overlay in case we get back something valid right away
      }, MS_GRACE_PERIOD_START_MS);
    };

    const endGracePeriod = () => {
      if (gracePeriodStartTimeoutRef.current) {
        clearTimeout(gracePeriodStartTimeoutRef.current);
        gracePeriodStartTimeoutRef.current = null;
      }

      if (gracePeriodRef.current) {
        clearTimeout(gracePeriodRef.current);
        gracePeriodRef.current = null;
      }
    };

    const updateWithGrace = (ready: boolean, callback: () => void) => {
      if (!ready) {
        startGracePeriod(() => {
          callback();
        });
        return;
      }

      setWaitingForData(false);
      callback();
      endGracePeriod();
    };

    const onMessage = (event: CustomEvent) => {
      const { data } = event.detail;
      setLastUpdated(new Date().toISOString());

      // fallback for Arcs running < 2.5
      const readyStates = data.ready_comps || data.data;

      switch (event.detail.type) {
        case WsMsgType.M2U_MsCurrent:
          updateWithGrace(data, () => setMs(data));
          break;
        case WsMsgType.M2U_ReadyToFire:
          updateWithGrace(!!readyStates, () => {
            setR2f(data);
            setLastMachineStatus(data.machine_status);
          });
          break;
        default:
          break;
      }
    };

    WebSocketHelper.on(WsMsgType.S2U_ForwardWs, onMessage);

    return () => {
      WebSocketHelper.remove(WsMsgType.S2U_ForwardWs, onMessage);
    };
  }, [machine?.machineID, queueState, current.token, inQueue]);

  const daemonTables = useMemo(() => {
    if (!r2f) {
      return [];
    }

    // fallback for Arcs running < 2.5
    if (!r2f.full_state) {
      return [
        {
          id: 'Machine State',
          label: 'Machine State',
          value: 'Machine State',
        },
      ];
    }

    const tableOptions: DaemonNameExt[] = [
      'Machine State',
      ...Object.values(DaemonName),
    ];

    // one section for each daemon + one section for the machine state (basic r2f/ms) overview
    const sections: IMachineStateSectionDef[] = tableOptions.map((table) => {
      return {
        id: table,
        label: table,
        value: table,
        status:
          (r2f.full_state?.[table]?.daemon_status as MachineStatus) ??
          r2f.daemon_statuses?.[table],
      };
    });

    // show status badge in dropdown for non-selected tables
    return sections.map((section) => {
      const isOverview = section.value === 'Machine State';

      return {
        ...section,
        suffix: section.status && !isOverview && (
          <Badge
            // we'll always have section.status unless Arc is running < 2.5
            color={getMachineStatusColor(section.status)}
            style={{ marginLeft: 'auto' }}
            ml={RADIX.FLEX.GAP.MD}
          >
            {section.status}
          </Badge>
        ),
      };
    });
  }, [r2f, activeTable]);

  useEffect(() => {
    if (ms && r2f) {
      setInitialized(true);
    }
  }, [ms, r2f]);

  if (!machine) {
    return null;
  }

  const lastUpdatedString =
    lastUpdated &&
    format(parseISO(lastUpdated), 'yyyy-MM-dd @ h:mm:ss a', {
      timeZone: LOCAL_TIMEZONE,
    });

  // add 'State' suffix to the title for Daemon tables
  const title =
    activeTable === 'Machine State'
      ? activeTable
      : t('admin.x-daemon-state', { x: activeTable });

  const showLoadingSpinner = machine.connected && !initialized;

  const showDaemonSelector =
    userRoleFeatureFlags.adminMachines.daemonStates &&
    machine.connected &&
    initialized &&
    daemonTables.length;

  const showViewErrorsButton =
    showDaemonSelector && activeTable !== 'Machine State';

  return (
    <Flex direction="column" gap={RADIX.FLEX.GAP.SM}>
      <Flex
        gap={RADIX.FLEX.GAP.SM}
        align="center"
        justify="between"
        wrap="wrap"
      >
        <Flex gap={RADIX.FLEX.GAP.SM} align="center">
          <Heading size="4">{title}</Heading>

          {lastUpdated && initialized && (
            <Badge color={RADIX.COLOR.NEUTRAL}>{lastUpdatedString}</Badge>
          )}
        </Flex>

        <Flex gap={RADIX.FLEX.GAP.SM} align="center">
          {showViewErrorsButton && (
            <Button
              variant="soft"
              color={RADIX.COLOR.NEUTRAL}
              onClick={() => viewSenderErrors(activeTable)}
            >
              View Errors
            </Button>
          )}
          {showDaemonSelector && (
            <CommonSelectInput
              id="daemon-table-select"
              name="daemon"
              placeholder="Daemon"
              options={daemonTables}
              value={activeTable}
              onChange={(v) => setActiveTable(v as DaemonNameExt)}
              disabled={!initialized || waitingForData}
              skipSort
            />
          )}
        </Flex>
        {showLoadingSpinner && <Spinner size="2" />}
      </Flex>

      {!machine.connected ? (
        <CommonCallout
          text={t('admin.machine-offline-msg').toString()}
          color={RADIX.COLOR.DANGER}
        />
      ) : (
        <Box
          style={{
            position: 'relative',
          }}
        >
          <StateTable
            activeTable={activeTable}
            ms={ms}
            r2f={r2f}
            loading={!initialized}
            lastTarget={props.lastTarget}
          />
          <BlurOverlay show={waitingForData} />
        </Box>
      )}
    </Flex>
  );
};
